/** The order record's section registry (plan §2.4, §5): every catalog field key → its
 *  spine panel (Placed · Contact · Bookings · Money), its position and its block (the
 *  Editable fields, the muted "From <restech>" vendor block, or the System block of
 *  derived / provenance values).
 *
 *  The same shape as the booking registry (`features/bookings/record/registry.ts`), so
 *  the records core lays both out alike. An order has no facets of its own — a tabs view
 *  of an order would be its four stages — so `facet` repeats the stage.
 *
 *  Order within a stage is the console's: the order's identity first (order ref and
 *  machine ID — the D10 fields, whose required choice and warnings render under them),
 *  then its state, references, account and tags.
 *
 *  Contract (registry.contract.test.ts): the keys are exactly the spec enum
 *  `OrderRecordFieldKey`, so a field the backend adds fails console CI until it is placed
 *  here. */
import { ORDER_RECORD_STAGES, type OrderRecordStage } from '@/api/orderRecord';
import type { FieldBlockKind, FieldRegistry, RegistryEntry } from '@/features/records/views/types';

export type OrderRegistryEntry = RegistryEntry<OrderRecordStage, OrderRecordStage>;

function at(stage: OrderRecordStage, block: FieldBlockKind, order: number): OrderRegistryEntry {
  return { stage, facet: stage, block, order };
}

const E = 'editable';
const V = 'vendor';
const S = 'system';

export const ORDER_RECORD_REGISTRY = {
  // ------------------------------------------------------------------- Placed
  order_ref: at('placed', E, 10),
  custom_id: at('placed', E, 20),
  booking_state: at('placed', E, 30),
  active: at('placed', E, 40),
  supplier_reference: at('placed', E, 50),
  reseller_ref: at('placed', E, 60),
  user: at('placed', E, 70),
  tags: at('placed', E, 80),

  // ------------------------------------------------------------------ Contact
  contact_first_name: at('contact', E, 10),
  contact_last_name: at('contact', E, 20),
  contact_email: at('contact', E, 30),
  contact_phone: at('contact', E, 40),
  contact_company: at('contact', E, 50),
  contact_country: at('contact', E, 60),

  // ----------------------------------------------------------------- Bookings
  bookings: at('bookings', S, 10),
  start_datetime: at('bookings', S, 20),
  end_datetime: at('bookings', S, 30),

  // -------------------------------------------------------------------- Money
  prices: at('money', V, 10),
  currency: at('money', V, 20),
  tax_amount: at('money', V, 30),
  settlement_method: at('money', V, 40),
  email_receipt: at('money', V, 50),
  remote_created_ts: at('money', V, 60),
  remote_updated_ts: at('money', V, 70),
  integration: at('money', S, 80),
  restech_account: at('money', S, 90),
  created_ts: at('money', S, 100),
  updated_ts: at('money', S, 110),
} as const satisfies FieldRegistry<OrderRecordStage, OrderRecordStage>;

/** Every catalog key the console places — the spec enum `OrderRecordFieldKey`. */
export type OrderRecordFieldKey = keyof typeof ORDER_RECORD_REGISTRY;

export type OrderRegistry = Readonly<Record<string, OrderRegistryEntry>>;

/** The D10 identity fields: their warnings (and, for the order ref, the linked-bookings
 *  question) render under them, so they get room of their own in the Placed panel. */
export const IDENTITY_KEYS: ReadonlySet<string> = new Set(['order_ref', 'custom_id']);

/** Panel titles, in lifecycle order. */
export const ORDER_STAGE_TITLES: Record<OrderRecordStage, string> = {
  placed: 'Placed',
  contact: 'Contact',
  bookings: 'Bookings',
  money: 'Money',
};

/** The keys grouped by stage, ordered by stage then position — the spine's placement.
 *  Pure. */
export function orderPlacements(registry: OrderRegistry): Map<OrderRecordStage, string[]> {
  const stageIndex = (s: OrderRecordStage) => ORDER_RECORD_STAGES.indexOf(s);
  const keys = Object.keys(registry).sort((a, b) => {
    const x = registry[a]!;
    const y = registry[b]!;
    return stageIndex(x.stage) - stageIndex(y.stage) || x.order - y.order;
  });
  const out = new Map<OrderRecordStage, string[]>();
  for (const key of keys) {
    const stage = registry[key]!.stage;
    const list = out.get(stage);
    if (list) list.push(key);
    else out.set(stage, [key]);
  }
  return out;
}
